import { create } from 'zustand';
import { applyCommand, applySmoothing, bucketFill, previewSmoothedBoundary } from '../core/editor';
import { export3MF } from '../core/exporter';
import { parse3MF } from '../core/parser';
import type { BoundaryChain, EditCommand, EditorDocument, SmoothOptions } from '../core/types';

type BoundaryMode = 'off' | 'smoothed' | 'compare';

interface AppState {
  document: EditorDocument | null;
  revision: number;
  status: string;
  error: string | null;
  selectedExtruder: number;
  slopeLimit: number;
  smoothOptions: SmoothOptions;
  boundaryMode: BoundaryMode;
  selectedBoundaryId: string | null;
  previewBoundaries: BoundaryChain[];
  undoStack: EditCommand[];
  redoStack: EditCommand[];
  openFile(file: File): Promise<void>;
  fill(partIndex: number, leafId: number): void;
  setExtruder(index: number): void;
  setSlope(value: number): void;
  setSmoothOption<K extends keyof SmoothOptions>(key: K, value: SmoothOptions[K]): void;
  setBoundaryMode(mode: BoundaryMode): void;
  selectBoundary(id: string | null): void;
  applySelectedSmooth(): void;
  applyAllSmooth(): void;
  undo(): void;
  redo(): void;
  exportFile(): Promise<Uint8Array | null>;
  clearError(): void;
}

function boundaryGroupFor(state: AppState, id: string | null) {
  if (!state.document || !id) return null;
  return state.document.boundaryGroups.find((group) => group.id === id || group.chainIds.includes(id)) ?? null;
}

function updatePreview(state: AppState, id = state.selectedBoundaryId): BoundaryChain[] {
  const group = boundaryGroupFor(state, id);
  if (!state.document || !group) return [];
  return group.chainIds.map((chainId) => previewSmoothedBoundary(state.document!, chainId, state.smoothOptions)).filter((chain): chain is BoundaryChain => !!chain);
}

export const useAppStore = create<AppState>((set, get) => ({
  document: null,
  revision: 0,
  status: 'Open an OrcaSlicer or Bambu Studio project 3MF',
  error: null,
  selectedExtruder: 1,
  slopeLimit: 30,
  smoothOptions: { strength: 70, maxMovement: 0.5, tolerance: 0.05, creaseAngle: 50 },
  boundaryMode: 'smoothed',
  selectedBoundaryId: null,
  previewBoundaries: [],
  undoStack: [],
  redoStack: [],

  async openFile(file) {
    if (!file.name.toLowerCase().endsWith('.3mf')) { set({ error: 'Choose a .3mf file.' }); return; }
    set({ status: `Reading ${file.name}…`, error: null });
    try {
      const document = await parse3MF(new Uint8Array(await file.arrayBuffer()), file.name);
      const selectedBoundaryId = document.boundaryGroups[0]?.id ?? null;
      const state = get();
      set({
        document, revision: state.revision + 1, selectedExtruder: document.filaments[0]?.index ?? 1,
        selectedBoundaryId, undoStack: [], redoStack: [], status: 'Ready', error: null,
      });
      set({ previewBoundaries: updatePreview(get(), selectedBoundaryId) });
    } catch (error) {
      set({ document: null, status: 'Open failed', error: error instanceof Error ? error.message : String(error) });
    }
  },

  fill(partIndex, leafId) {
    const state = get(); if (!state.document) return;
    try {
      const command = bucketFill(state.document, partIndex, leafId, state.selectedExtruder, state.slopeLimit);
      if (!command) { set({ status: 'No paint change' }); return; }
      set({ revision: state.revision + 1, undoStack: [...state.undoStack, command], redoStack: [],
        selectedBoundaryId: null, previewBoundaries: [], status: command.label });
    } catch (error) { set({ error: error instanceof Error ? error.message : String(error) }); }
  },

  setExtruder(selectedExtruder) { set({ selectedExtruder }); },
  setSlope(slopeLimit) { set({ slopeLimit }); },
  setSmoothOption(key, value) {
    const smoothOptions = { ...get().smoothOptions, [key]: value };
    set({ smoothOptions });
    set({ previewBoundaries: updatePreview(get()) });
  },
  setBoundaryMode(boundaryMode) { set({ boundaryMode }); },
  selectBoundary(id) {
    const selectedBoundaryId = boundaryGroupFor(get(), id)?.id ?? null;
    set({ selectedBoundaryId });
    set({ previewBoundaries: updatePreview(get(), selectedBoundaryId) });
  },
  applySelectedSmooth() {
    const state = get();
    if (!state.document || !state.selectedBoundaryId) return;
    const group = boundaryGroupFor(state, state.selectedBoundaryId);
    if (!group) return;
    set({ status: 'Smoothing boundary…' });
    try {
      const command = applySmoothing(state.document, group.chainIds, state.smoothOptions);
      if (!command) { set({ status: 'Boundary unchanged' }); return; }
      command.label = 'Smooth boundary';
      set({ revision: state.revision + 1, undoStack: [...state.undoStack, command], redoStack: [],
        selectedBoundaryId: null, previewBoundaries: [], status: command.label });
    } catch (error) { set({ status: 'Smoothing failed', error: error instanceof Error ? error.message : String(error) }); }
  },
  applyAllSmooth() {
    const state = get(); if (!state.document) return;
    const ids = state.document.boundaries.map((chain) => chain.id);
    set({ status: `Smoothing ${state.document.boundaryGroups.length} boundaries…` });
    try {
      const command = applySmoothing(state.document, ids, state.smoothOptions);
      if (!command) { set({ status: 'No smoothable boundaries' }); return; }
      command.label = 'Smooth all boundaries';
      set({ revision: state.revision + 1, undoStack: [...state.undoStack, command], redoStack: [],
        selectedBoundaryId: null, previewBoundaries: [], status: command.label });
    } catch (error) { set({ status: 'Smoothing failed', error: error instanceof Error ? error.message : String(error) }); }
  },
  undo() {
    const state = get(); const command = state.undoStack.at(-1); if (!state.document || !command) return;
    applyCommand(state.document, command, 'undo');
    set({ revision: state.revision + 1, undoStack: state.undoStack.slice(0, -1), redoStack: [...state.redoStack, command],
      selectedBoundaryId: null, previewBoundaries: [], status: `Undo: ${command.label}` });
  },
  redo() {
    const state = get(); const command = state.redoStack.at(-1); if (!state.document || !command) return;
    applyCommand(state.document, command, 'redo');
    set({ revision: state.revision + 1, redoStack: state.redoStack.slice(0, -1), undoStack: [...state.undoStack, command],
      selectedBoundaryId: null, previewBoundaries: [], status: `Redo: ${command.label}` });
  },
  async exportFile() {
    const state = get(); if (!state.document) return null;
    set({ status: state.document.dirty ? 'Generating and validating 3MF…' : 'Preparing original 3MF…', error: null });
    try {
      const output = await export3MF(state.document);
      set({ status: state.document.dirty ? 'Export ready' : 'Original file ready (no changes)' });
      return output;
    } catch (error) {
      set({ status: 'Export failed', error: error instanceof Error ? error.message : String(error) });
      return null;
    }
  },
  clearError() { set({ error: null }); },
}));
